import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment-timezone'

import { splitTextIntoPosts } from '@/lib/scheduling'
import { cloneGallery } from '@/lib/utils'

import submitPlatformPost from '@/graphql/mutations/submissions/submitPlatformPost.gql'
import reschedulePlatformPost from '@/graphql/mutations/submissions/reschedulePlatformPost.gql'
import removeSubmissionFromPlatform from '@/graphql/mutations/submissions/removeSubmissionFromPlatform.gql'
import deletePlatformPost from '@/graphql/mutations/submissions/deletePlatformPost.gql'
import duplicatePlatformPost from '@/graphql/mutations/submissions/duplicatePlatformPost.gql'
import cancelPlatformAutoRepost from '@/graphql/mutations/submissions/cancelPlatformAutoRepost.gql'
import cancelPlatformAutoRemove from '@/graphql/mutations/submissions/cancelPlatformAutoRemove.gql'

import mastodonPost from '@/graphql/queries/mastodon/mastodonPost.gql'
import allMastodonSubmissions from '@/graphql/queries/mastodon/allMastodonSubmissions.gql'

const MAX_SUBMISSION_LENGTH = 500

export default {
  code: 'mastodon',
  name: 'Mastodon',
  icon: 'mastodon',
  color: '#6364ff',
  postNoun: 'Post',
  internalPostNoun: 'MastodonPost',
  repostNoun: 'Boost',
  accountNoun: 'Account',
  mutations: {
    submitPost: submitPlatformPost,
    submitPostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    deletePost: deletePlatformPost,
    deletePostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    reschedulePost: reschedulePlatformPost,
    reschedulePostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    duplicatePost: duplicatePlatformPost,
    removeSubmissionFromPlatformMutation: removeSubmissionFromPlatform,
    cancelAutoRepostMutation: cancelPlatformAutoRepost,
    cancelAutoRemoveMutation: cancelPlatformAutoRemove
  },
  queries: {
    allSubmissions: allMastodonSubmissions,
    getPost: mastodonPost
  },
  posts: {
    preparePostForScheduling: (post, publishingStatus = 'READY_TO_PUBLISH') => {
      let thread = []

      // Sync auto-remove
      const firstSubmission = post.thread[0]
      if (!!firstSubmission) {
        for (let s of post.thread) {
          s.removeAtAmount = firstSubmission.removeAtAmount
          s.removeAtUnit = firstSubmission.removeAtUnit
          s.removeMinLikes = firstSubmission.removeMinLikes
        }
      }

      // Deep copy the thread so that we don't modify underlying gallery
      // objects, which breaks image previews.
      const formThread = cloneDeep(post.thread)

      for (let submission of formThread) {
        let gallery = null
        if (submission.gallery) {
          gallery = {
            id: submission.gallery.id,
            galleryMediaSet: []
          }
          gallery.galleryMediaSet = submission.gallery.galleryMediaSet.map(
            (mediaSet) => {
              return {
                id: mediaSet.id,
                order: mediaSet.order,
                media: {
                  name: mediaSet.media.name,
                  altText: mediaSet.media.altText,
                  url: mediaSet.media.url,
                  externalId: mediaSet.media.externalId,
                  mimeType: mediaSet.media.mimeType,
                  size: mediaSet.media.size,
                  source: mediaSet.media.source
                }
              }
            }
          )
        }

        thread.push({
          id: submission.id,
          text: submission.text,
          contentWarning: submission.contentWarning,
          language: submission.language,
          visibility: post.visibility,
          gallery: gallery,
          order: submission.order,
          repostAtAmount: submission.repostAtAmount,
          repostAtUnit: submission.repostAtUnit,
          repostRepeatDays: submission.repostRepeatDays,
          repostFromSocialAccount: getRepostFromSocialAccount(submission),
          removeAtAmount: submission.removeAtAmount,
          removeAtUnit: submission.removeAtUnit,
          removeMinLikes: submission.removeMinLikes,
          final: submission.final
        })
      }

      return {
        id: post.id,
        username: post.username,
        thread: thread,
        postAt: post.postAt,
        publishingStatus
      }
    },
    createPost(socialAccount) {
      let post = {
        id: null,
        username: socialAccount.username,
        thread: [],
        postAt: moment().add(1, 'hour').startOf('hour').toDate(),
        visibility: 'PUBLIC',
        publishingStatus: 'READY_TO_PUBLISH'
      }

      post.thread.push(newSubmission(post, 0))

      return post
    },
    getPost(post) {
      let thread = post.thread || post.submissions
      let firstPost = thread[0]
      return {
        caption: '',
        text: firstPost.text,
        gallery: cloneGallery(firstPost.gallery),
        postAt: moment(firstPost.postAt).toDate(),
        thread: thread.map((s) => {
          return {
            text: s.text,
            gallery: cloneGallery(s.gallery)
          }
        })
      }
    },
    syncField(post, field, value, socialAccount) {
      if (field === 'caption' || field === 'text') {
        if (value.length <= MAX_SUBMISSION_LENGTH) {
          if (post.thread.length > 0) {
            post.thread[0].text = value
          }
        } else {
          const paragraphs = splitTextIntoPosts(value, MAX_SUBMISSION_LENGTH)
          paragraphs.forEach((paragraph, index) => {
            let hasPostAtIndex = post.thread.length > index
            if (hasPostAtIndex) {
              post.thread[index].text = paragraph
            } else {
              post.thread.push({
                ...newSubmission(post, post.thread.length),
                text: paragraph
              })
            }
          })
          if (paragraphs.length < post.thread.length) {
            post.thread = post.thread.slice(0, paragraphs.length)
          }
        }
      } else if (field === 'gallery') {
        if (post.thread.length > 0) {
          post.thread[0].gallery = cloneDeep(value)
        }
      } else if (field === 'postAt') {
        post.postAt = value
      } else if (field === 'thread') {
        value.forEach((s, index) => {
          if (s.final) {
            // Do not sync final tweets.
            return
          }
          let hasPostAtIndex = post.thread.length > index
          if (hasPostAtIndex) {
            post.thread[index].text = s.text
            post.thread[index].gallery = cloneDeep(s.gallery)
          } else {
            let submission = newSubmission(post, post.thread.length)
            submission.text = s.text
            submission.gallery = s.gallery
            post.thread.push(submission)
          }
        })
        if (value.length < post.thread.length) {
          post.thread = post.thread.slice(0, value.length)
        }
      }

      return post
    }
  },
  analytics: {
    metrics: [
      {
        metric: 'likes',
        label: 'Likes',
        icon: 'heart',
        aggregation: 'sum'
      },
      {
        metric: 'replies',
        label: 'Replies',
        icon: 'comment',
        aggregation: 'sum'
      },
      {
        metric: 'reposts',
        label: 'Reposts',
        icon: 'retweet',
        aggregation: 'sum'
      },
      {
        metric: 'posts',
        label: 'Posts',
        icon: 'paper-plane',
        aggregation: 'sum'
      },
      {
        metric: 'likes',
        label: 'Avg. Likes',
        aggregation: 'avg'
      }
    ]
  }
}

function newSubmission(post, order) {
  return {
    id: null,
    order,
    text: '',
    contentWarning: '',
    language: '',
    visibility: post.visibility,
    gallery: null,
    postAt: post.postAt,
    final: false
  }
}

function getRepostFromSocialAccount(submission) {
  if (
    submission.repostFromSocialAccount &&
    submission.repostFromSocialAccount.username
  ) {
    // Get rid of the `__typename` property.
    return {
      username: submission.repostFromSocialAccount.username,
      platform: submission.repostFromSocialAccount.platform
    }
  }
  return null
}
